import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import CardContent from '@mui/material/CardContent';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Box from '@mui/material/Box';
import TuneIcon from '@mui/icons-material/Tune';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';

import MainCard from 'ui-component/cards/MainCard';
import { gridSpacing } from 'store/constant';
import config, { ThemeMode } from 'config';

// assets
import BudgetFilters from './components/BudgetFilters';
import InfluencerFilters from './components/InfluencerFilters';
import AudienceFilters from './components/AudienceFilters';
import InternalTeamFilters from './components/InternalTeamFilters';

import { fetchInfluencersAction } from 'actions/planner2';
import { AdvFilterV2Context } from './filterContext';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <div>{children}</div>}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

// tabs option
const tabsOption = [
  { label: 'Budget', icon: PaymentsOutlinedIcon },
  { label: 'Engagement', icon: TrendingUpIcon },
  { label: 'Audience', icon: GroupsOutlinedIcon },
  { label: 'Internal Team', icon: ShieldOutlinedIcon },
];

export default function AdvancedFilters({ v2, open, setOpen, bcRosterOnly, setBcRosterOnly, bcUniqueOptions, platform }) {
  const dispatch = useDispatch();
  const { mode } = config;
  const handleClose = () => {
    setOpen(false);
  };

  const user = useSelector((state) => state.planner2.user);
  const [value, setValue] = React.useState(0);

  const filteredTabsOption = user.is_staff
  ? tabsOption.filter((tab) => tab) // Show only Internal Team tab
  : tabsOption.filter((tab) => tab.label !== 'Internal Team'); // Show all tabs except Internal Team


  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  return (
    <React.Fragment>
      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
        fullWidth
        PaperProps={v2 ? { className: 'dv2-adv-paper' } : undefined}
      >
        <DialogTitle
          sx={{ m: 0, p: v2 ? '14px 20px' : 2, display: 'flex', alignItems: 'center', gap: 1.25 }}
          id="customized-dialog-title"
        >
          {v2 && (
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '9px',
                background: 'linear-gradient(135deg,#111936,#26315f)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <TuneIcon sx={{ color: '#febd1c', fontSize: 18 }} />
            </Box>
          )}
          <span style={v2 ? { fontWeight: 800, fontSize: 18, color: '#111936' } : {}}>
            Advanced Filters
          </span>
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            // color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers>
         <AdvFilterV2Context.Provider value={!!v2}>
          <Grid container spacing={gridSpacing}>
            <Grid item xs={12}>
              <MainCard content={false}>
                <Grid container spacing={gridSpacing}>
                  <Grid item xs={12} lg={12}>
                    <CardContent sx={{padding: 0}}>
                      <Tabs
                        value={value}
                        variant="scrollable"
                        onChange={handleChange}
                        sx={
                          v2
                            ? {
                                mb: 0,
                                minHeight: 0,
                                borderBottom: '1px solid #e8ecf4',
                                '& .MuiTabs-indicator': {
                                  height: 3,
                                  borderRadius: '3px 3px 0 0',
                                  backgroundColor: '#febd1c',
                                },
                                '& .MuiTab-root': {
                                  minHeight: 46,
                                  minWidth: 0,
                                  px: 1.5,
                                  mr: 1.5,
                                  textTransform: 'none',
                                  fontWeight: 600,
                                  fontSize: 13.5,
                                  color: '#5b6478',
                                  transition: 'color .15s ease',
                                },
                                '& .MuiTab-root:hover': { color: '#111936' },
                                '& .MuiTab-root.Mui-selected': {
                                  color: '#c8900b',
                                },
                                '& .MuiTab-iconWrapper': { mr: 0.75, mb: '0 !important' },
                              }
                            : {
                                mb: 3,
                                '& a': {
                                  minHeight: 'auto',
                                  minWidth: 10,
                                  py: 1.5,
                                  px: 1,
                                  mr: 2.2,
                                  color: mode === ThemeMode.DARK ? 'grey.600' : 'grey.900',
                                  display: 'flex',
                                  flexDirection: 'row',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                },
                                '& a.Mui-selected': {
                                  color: 'primary.main',
                                },
                                '& a > svg': {
                                  mb: '0px !important',
                                  mr: 1.1,
                                },
                              }
                        }
                    >
                        {filteredTabsOption.map((tab, index) => {
                          const TabIcon = tab.icon;
                          return v2 ? (
                            <Tab
                              key={index}
                              icon={<TabIcon sx={{ fontSize: 18 }} />}
                              iconPosition="start"
                              label={tab.label}
                              {...a11yProps(index)}
                            />
                          ) : (
                            <Tab
                              key={index}
                              label={
                                <Grid container direction="column">
                                  <Typography variant="subtitle1" color="inherit">
                                    {tab.label}
                                  </Typography>
                                </Grid>
                              }
                              {...a11yProps(index)}
                            />
                          );
                        })}
                        </Tabs>
                        <TabPanel value={value} index={0}>
                          <BudgetFilters />
                        </TabPanel>
                        <TabPanel value={value} index={1}>
                          <InfluencerFilters />
                        </TabPanel>
                        <TabPanel value={value} index={2}>
                          <AudienceFilters bcRosterOnly={bcRosterOnly} bcUniqueOptions={bcUniqueOptions} platform={platform} />
                        </TabPanel>
                        {user.is_staff ? (
                          <TabPanel value={value} index={3}>
                            <InternalTeamFilters bcRosterOnly={bcRosterOnly} setBcRosterOnly={setBcRosterOnly} platform={platform} />
                          </TabPanel>
                        ): ""}
                    </CardContent>
                  </Grid>
                  {/* <Grid item xs={12} lg={8}>
                    <CardContent
                      sx={{
                        borderLeft: '1px solid',
                        borderColor: mode === ThemeMode.DARK ? 'background.default' : 'grey.200',
                        height: '100%',
                      }}
                    >
                      
                    </CardContent>
                  </Grid> */}
                </Grid>
              </MainCard>
            </Grid>
          </Grid>
         </AdvFilterV2Context.Provider>
        </DialogContent>
        <DialogActions sx={v2 ? { px: 2.5, py: 1.5 } : undefined}>
          {v2 && (
            <Button onClick={handleClose} sx={{ textTransform: 'none', color: '#5b6478', fontWeight: 600 }}>
              Cancel
            </Button>
          )}
          <Button
            autoFocus
            variant={v2 ? 'contained' : 'text'}
            onClick={() => {
              dispatch(fetchInfluencersAction(1));
              handleClose();
            }}
            sx={
              v2
                ? {
                    textTransform: 'none',
                    fontWeight: 700,
                    px: 3,
                    background: '#febd1c',
                    color: '#111936',
                    boxShadow: '0 6px 14px -6px rgba(254,189,28,0.8)',
                    '&:hover': { background: '#f0b016' },
                  }
                : undefined
            }
          >
            Apply Filters
          </Button>
        </DialogActions>
      </BootstrapDialog>
    </React.Fragment>
  );
}
